import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  register: (data) => axios.post(`${API}/auth/register`, data),
  login: (data) => axios.post(`${API}/auth/login`, data),
  getProfile: () => axios.get(`${API}/auth/profile`, { headers: getAuthHeader() }),
  getAccessConfig: () => axios.get(`${API}/auth/access-config`),

  // Children
  createChild: (data) => axios.post(`${API}/children`, data, { headers: getAuthHeader() }),
  getChildren: () => axios.get(`${API}/children`, { headers: getAuthHeader() }),
  getChild: (id) => axios.get(`${API}/children/${id}`, { headers: getAuthHeader() }),

  // Screenings
  createScreening: (data) => axios.post(`${API}/screenings`, data, { headers: getAuthHeader() }),
  getChildScreenings: (childId) => axios.get(`${API}/screenings/child/${childId}`, { headers: getAuthHeader() }),

  // Interventions
  generateIntervention: (screeningId) => axios.post(`${API}/interventions/generate?screening_id=${screeningId}`, {}, { headers: getAuthHeader() }),
  getChildInterventions: (childId) => axios.get(`${API}/interventions/child/${childId}`, { headers: getAuthHeader() }),

  // Dashboards
  getCaregiverDashboard: () => axios.get(`${API}/dashboard/caregiver`, { headers: getAuthHeader() }),
  getFrontlineDashboard: () => axios.get(`${API}/dashboard/frontline`, { headers: getAuthHeader() }),
  getAdminDashboard: () => axios.get(`${API}/dashboard/admin`, { headers: getAuthHeader() }),

  // Admin State/District Dashboards
  getStates: () => axios.get(`${API}/admin/states`, { headers: getAuthHeader() }),
  getDistricts: (state) => axios.get(`${API}/admin/districts/${encodeURIComponent(state)}`, { headers: getAuthHeader() }),
  getStateDashboard: (state) => axios.get(`${API}/admin/dashboard/state/${encodeURIComponent(state)}`, { headers: getAuthHeader() }),
  getDistrictDashboard: (state, district) => axios.get(`${API}/admin/dashboard/district/${encodeURIComponent(state)}/${encodeURIComponent(district)}`, { headers: getAuthHeader() }),

  // Alerts
  getAlerts: () => axios.get(`${API}/alerts`, { headers: getAuthHeader() }),
  updateAlertStatus: (alertId, status) => axios.patch(`${API}/alerts/${alertId}/status?status=${status}`, {}, { headers: getAuthHeader() }),

  // Admin User Management
  getUsers: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    if (params.role) queryParams.append('role', params.role);
    if (params.search) queryParams.append('search', params.search);
    return axios.get(`${API}/admin/users?${queryParams.toString()}`, { headers: getAuthHeader() });
  },
  adminResetPassword: (userId) => axios.post(`${API}/admin/reset-user-password`, { user_id: userId }, { headers: getAuthHeader() }),

  // Progress Tracking
  getChildProgress: (childId) => axios.get(`${API}/children/${childId}/progress`, { headers: getAuthHeader() }),

  // Notifications
  getNotifications: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    if (params.notification_type) queryParams.append('notification_type', params.notification_type);
    if (params.child_id) queryParams.append('child_id', params.child_id);
    return axios.get(`${API}/notifications?${queryParams.toString()}`, { headers: getAuthHeader() });
  },
  getChildNotifications: (childId) => axios.get(`${API}/notifications/child/${childId}`, { headers: getAuthHeader() }),
  sendFollowUpReminder: (childId) => axios.post(`${API}/notifications/send-follow-up-reminder/${childId}`, {}, { headers: getAuthHeader() }),

  // Bulk Upload Users (Frontline Workers)
  getUserUploadTemplate: () => axios.get(`${API}/users/bulk-upload/template`, { 
    headers: getAuthHeader(),
    responseType: 'blob'
  }),
  bulkUploadUsers: (file, sendEmails = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/users/bulk-upload?send_emails=${sendEmails}`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Super Admin Dashboard
  getSuperAdminDashboard: () => axios.get(`${API}/dashboard/super-admin`, { headers: getAuthHeader() }),
  
  // District Management
  createDistrict: (data) => axios.post(`${API}/admin/districts`, data, { headers: getAuthHeader() }),
  updateDistrict: (districtId, data) => axios.put(`${API}/admin/districts/${districtId}`, data, { headers: getAuthHeader() }),
  deleteDistrict: (districtId) => axios.delete(`${API}/admin/districts/${districtId}`, { headers: getAuthHeader() }),
  
  // Dynamic Recommendations
  getDynamicRecommendations: (childId) => axios.get(`${API}/children/${childId}/dynamic-recommendations`, { headers: getAuthHeader() }),

  // Organizations / Multi-tenant
  getAvailableStates: () => axios.get(`${API}/organizations/states`),
  getOrganizations: () => axios.get(`${API}/organizations`, { headers: getAuthHeader() }),
  initializeOrganizations: () => axios.post(`${API}/organizations/initialize`, {}, { headers: getAuthHeader() }),
  getOrganizationUsers: (stateCode, role) => {
    const params = role ? `?role=${role}` : '';
    return axios.get(`${API}/organizations/${stateCode}/users${params}`, { headers: getAuthHeader() });
  },
  getOrganizationStats: (stateCode) => axios.get(`${API}/organizations/${stateCode}/statistics`, { headers: getAuthHeader() }),
  assignUserToOrganization: (userId, stateCode, districts = []) => 
    axios.post(`${API}/organizations/assign-user?user_id=${userId}&state_code=${stateCode}`, { districts }, { headers: getAuthHeader() }),

  // Districts by State
  getDistrictsByState: (stateCode) => axios.get(`${API}/organizations/${stateCode}/districts`, { headers: getAuthHeader() }),

  // State Admin Dashboard (District-level analytics)
  getStateAdminDashboard: (stateCode) => axios.get(`${API}/dashboard/state-admin/${stateCode}`, { headers: getAuthHeader() }),
  
  // District Admin Dashboard (Taluk/Village-level analytics)
  getDistrictAdminDashboard: (stateCode, district) => axios.get(`${API}/dashboard/district-admin/${stateCode}/${encodeURIComponent(district)}`, { headers: getAuthHeader() }),

  // First-Time Setup (Super Admin bootstrap)
  checkFirstTimeSetup: () => axios.get(`${API}/setup/check`),
  createFirstSuperAdmin: (data) => axios.post(`${API}/setup/create-super-admin`, data),

  // Admin User Creation (Role-based)
  adminCreateUser: (data) => axios.post(`${API}/admin/create-user`, data, { headers: getAuthHeader() }),
  
  // Bulk Upload with Role
  getUserUploadTemplateByRole: (role) => axios.get(`${API}/admin/users/bulk-upload/template?role=${role}`, { 
    headers: getAuthHeader(),
    responseType: 'blob'
  }),
  bulkUploadUsersByRole: (file, role, sendEmails = true) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/admin/users/bulk-upload?role=${role}&send_emails=${sendEmails}`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Risk Notifications
  getRiskNotifications: (page = 1, pageSize = 20, riskLevel = null, isRead = null) => {
    let params = `page=${page}&page_size=${pageSize}`;
    if (riskLevel) params += `&risk_level=${riskLevel}`;
    if (isRead !== null) params += `&is_read=${isRead}`;
    return axios.get(`${API}/risk-notifications?${params}`, { headers: getAuthHeader() });
  },
  getUnreadNotificationCount: () => axios.get(`${API}/risk-notifications/unread-count`, { headers: getAuthHeader() }),
  markNotificationRead: (notificationId) => axios.put(`${API}/risk-notifications/${notificationId}/read`, {}, { headers: getAuthHeader() }),
  markAllNotificationsRead: () => axios.put(`${API}/risk-notifications/read-all`, {}, { headers: getAuthHeader() }),
  getRiskNotificationSummary: () => axios.get(`${API}/risk-notifications/summary`, { headers: getAuthHeader() }),
  sendDailyDigest: () => axios.post(`${API}/risk-notifications/send-daily-digest`, {}, { headers: getAuthHeader() }),

  // Speech Analysis
  getSpeechActivities: (childId) => axios.get(`${API}/speech/activities/${childId}`, { headers: getAuthHeader() }),
  getSpeechMilestones: () => axios.get(`${API}/speech/milestones`, { headers: getAuthHeader() }),
  getSpeechLanguages: () => axios.get(`${API}/speech/languages`, { headers: getAuthHeader() }),
  createSpeechSession: (childId, activityId, language = 'en') => axios.post(`${API}/speech/session/create`, { child_id: childId, activity_id: activityId, language: language }, { headers: getAuthHeader() }),
  uploadSpeechAudio: (sessionId, audioFile) => {
    const formData = new FormData();
    formData.append('audio_file', audioFile);
    return axios.post(`${API}/speech/session/${sessionId}/upload-audio`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getSpeechSession: (sessionId) => axios.get(`${API}/speech/session/${sessionId}`, { headers: getAuthHeader() }),
  getChildSpeechSessions: (childId) => axios.get(`${API}/speech/sessions/child/${childId}`, { headers: getAuthHeader() }),
  getRecentSpeechSessions: (limit = 20) => axios.get(`${API}/speech/sessions/recent?limit=${limit}`, { headers: getAuthHeader() }),
  getSpeechReport: (sessionId) => axios.get(`${API}/speech/report/${sessionId}`, { headers: getAuthHeader() }),

  // ICDS Export Reports
  exportICDSReport: (state = null, district = null) => {
    let params = [];
    if (state) params.push(`state=${encodeURIComponent(state)}`);
    if (district) params.push(`district=${encodeURIComponent(district)}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return axios.get(`${API}/reports/icds-export${queryString}`, { 
      headers: getAuthHeader(),
      responseType: 'blob'
    });
  },
};

export default api;